import React, { useState } from 'react';
import { Container, Row, Col, Card, Button, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import Swal from 'sweetalert2';
import API_URL from '../config/api';
import { FaMobileAlt, FaDesktop, FaTv, FaBullseye, FaShoppingCart, FaPaintBrush, FaChartLine, FaSync, FaCloud, FaBuilding, FaStore, FaTicketAlt, FaCheckCircle } from 'react-icons/fa';
import useScrollAnimation from '../hooks/useScrollAnimation';

const PantallasTactiles = () => {
  const { t } = useTranslation();
  useScrollAnimation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    screenType: '',
    appNeeds: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API_URL}/contactos`, {
        asunto: 'Alquiler de Pantallas Táctiles - Consulta formulario web',
        nombre: formData.name,
        email: formData.email,
        telefono: formData.phone,
        mensaje: `Empresa: ${formData.company}\nTipo de pantalla: ${formData.screenType}\nNecesidades de app: ${formData.appNeeds}\n\n${formData.message}`
      });

      await Swal.fire({
        icon: 'success',
        title: t('contactForm.alert_title'),
        text: t('contactForm.alert_success'),
        confirmButtonColor: '#1E5096'
      });

      setFormData({ name: '', email: '', phone: '', company: '', screenType: '', appNeeds: '', message: '' });
    } catch (error) {
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Error enviando el mensaje. Por favor intenta nuevamente.',
        confirmButtonColor: '#1E5096'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const screenTypes = [
    { icon: FaTv, key: 'ledScreens' },
    { icon: FaMobileAlt, key: 'touchScreens' },
    { icon: FaDesktop, key: 'totem' },
    { icon: FaDesktop, key: 'interactiveTotems' },
    { icon: FaBullseye, key: 'mapping' },
    { icon: FaShoppingCart, key: 'retail' }
  ];

  const appFeatures = [
    { icon: FaPaintBrush, key: 'customDesign' },
    { icon: FaChartLine, key: 'analytics' },
    { icon: FaSync, key: 'realtime' },
    { icon: FaCloud, key: 'cloud' }
  ];

  return (
    <div id="pantallas-tactiles">
      {/* Hero Section */}
      <section className="py-5 text-white spotlight-effect" style={{
        background: 'linear-gradient(135deg, rgba(30, 80, 150, 0.9) 0%, rgba(22, 61, 115, 0.9) 100%), url("/stand-spacegym.png") center/cover no-repeat',
        minHeight: '50vh',
        display: 'flex',
        alignItems: 'center'
      }}>
        <Container>
          <Row className="align-items-center">
            <Col lg={8} className="mx-auto text-center animate-on-scroll">
              <h1 className="display-4 fw-bold mb-3">{t('screens.title')}</h1>
              <p className="fs-5 mb-2">{t('screens.subtitle')}</p>
              <p className="fs-6 mb-4 opacity-75">{t('screens.customApps')}</p>
              <Button
                variant="outline-light"
                size="lg"
                onClick={() => document.getElementById('quick-contact').scrollIntoView({ behavior: 'smooth' })}
              >
                {t('screens.requestQuote')}
              </Button>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Screen Types */}
      <section className="py-5">
        <Container>
          <Row className="text-center mb-5">
            <Col>
              <h2 style={{ color: '#1E5096' }}>{t('screens.screenTypes')}</h2>
              <p className="text-muted fs-5">{t('screens.screenTypesDescription')}</p>
            </Col>
          </Row>
          <Row className="justify-content-center">
            {screenTypes.map((item, index) => (
              <Col key={index} xs={6} md={4} lg={2} className="mb-4">
                <Card className="h-100 text-center border-0 shadow-sm p-3">
                  <Card.Body>
                    <div><item.icon size={40} color="#1E5096" /></div>
                    <Card.Title className="h6 mt-2" style={{ color: '#1E5096' }}>
                      {t(`screens.types.${item.key}`)}
                    </Card.Title>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Custom Apps Section */}
      <section className="py-5" style={{ background: '#f8f9fa' }}>
        <Container>
          <Row className="align-items-center">
            <Col lg={6} className="mb-4 mb-lg-0">
              <h2 style={{ color: '#1E5096' }}>{t('screens.appsTitle')}</h2>
              <p className="text-muted fs-5 mb-4">{t('screens.appsDescription')}</p>
              <Row>
                {appFeatures.map((feature, index) => (
                  <Col xs={6} key={index} className="mb-3">
                    <div className="d-flex align-items-center">
                      <span style={{ marginRight: '10px' }}><feature.icon size={28} color="#1E5096" /></span>
                      <span style={{ color: '#1E5096', fontWeight: '500' }}>
                        {t(`screens.features.${feature.key}`)}
                      </span>
                    </div>
                  </Col>
                ))}
              </Row>
            </Col>
            <Col lg={6}>
              <div className="p-5" style={{
                background: 'linear-gradient(135deg, #1E5096 0%, #163d73 100%)',
                borderRadius: '15px',
                color: 'white'
              }}>
                <h3 className="mb-4">{t('screens.techTitle')}</h3>
                <ul className="list-unstyled">
                  <li className="mb-2 d-flex align-items-center"><FaCheckCircle className="me-2 flex-shrink-0" /> {t('screens.tech1')}</li>
                  <li className="mb-2 d-flex align-items-center"><FaCheckCircle className="me-2 flex-shrink-0" /> {t('screens.tech2')}</li>
                  <li className="mb-2 d-flex align-items-center"><FaCheckCircle className="me-2 flex-shrink-0" /> {t('screens.tech3')}</li>
                  <li className="mb-2 d-flex align-items-center"><FaCheckCircle className="me-2 flex-shrink-0" /> {t('screens.tech4')}</li>
                </ul>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Use Cases */}
      <section className="py-5">
        <Container>
          <Row className="text-center mb-5">
            <Col>
              <h2 style={{ color: '#1E5096' }}>{t('screens.useCases')}</h2>
            </Col>
          </Row>
          <Row className="justify-content-center">
            <Col md={4} className="mb-4">
              <Card className="h-100 border-0 shadow-sm text-center p-4">
                <Card.Body>
                  <div><FaBuilding size={48} color="#1E5096" /></div>
                  <h5 className="mt-3" style={{ color: '#1E5096' }}>{t('screens.useCase1')}</h5>
                  <p className="text-muted">{t('screens.useCase1Desc')}</p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4} className="mb-4">
              <Card className="h-100 border-0 shadow-sm text-center p-4">
                <Card.Body>
                  <div><FaTicketAlt size={48} color="#1E5096" /></div>
                  <h5 className="mt-3" style={{ color: '#1E5096' }}>{t('screens.useCase2')}</h5>
                  <p className="text-muted">{t('screens.useCase2Desc')}</p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4} className="mb-4">
              <Card className="h-100 border-0 shadow-sm text-center p-4">
                <Card.Body>
                  <div><FaStore size={48} color="#1E5096" /></div>
                  <h5 className="mt-3" style={{ color: '#1E5096' }}>{t('screens.useCase3')}</h5>
                  <p className="text-muted">{t('screens.useCase3Desc')}</p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Quick Contact Form */}
      <section id="quick-contact" className="py-5 contact-section-bg">
        <Container>
          <Row className="justify-content-center">
            <Col lg={8}>
              <Card className="border-0 shadow">
                <Card.Body className="p-5">
                  <h2 className="text-center mb-4" style={{ color: '#1E5096' }}>
                    {t('screens.contactTitle')}
                  </h2>
                  <p className="text-center text-muted mb-4">{t('screens.contactSubtitle')}</p>

                  <Form onSubmit={handleSubmit}>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t('contactForm.name')} *</Form.Label>
                          <Form.Control
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t('contactForm.email')} *</Form.Label>
                          <Form.Control
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t('contactForm.phone')}</Form.Label>
                          <Form.Control
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t('screens.company')}</Form.Label>
                          <Form.Control
                            type="text"
                            name="company"
                            value={formData.company}
                            onChange={handleChange}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t('screens.screenType')}</Form.Label>
                          <Form.Select
                            name="screenType"
                            value={formData.screenType}
                            onChange={handleChange}
                          >
                            <option value="">{t('screens.selectType')}</option>
                            <option value="kiosk">{t('screens.types.kiosk')}</option>
                            <option value="totem">{t('screens.types.totem')}</option>
                            <option value="videowall">{t('screens.types.videowall')}</option>
                            <option value="interactive">{t('screens.types.interactive')}</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Form.Group className="mb-3">
                      <Form.Label>{t('screens.appNeeds')}</Form.Label>
                      <Form.Control
                        type="text"
                        name="appNeeds"
                        value={formData.appNeeds}
                        onChange={handleChange}
                        placeholder={t('screens.appNeedsPlaceholder')}
                      />
                    </Form.Group>
                    <Form.Group className="mb-4">
                      <Form.Label>{t('contactForm.message')} *</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={4}
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder={t('screens.messagePlaceholder')}
                        required
                      />
                    </Form.Group>
                    <div className="text-center">
                      <Button
                        variant="primary"
                        type="submit"
                        size="lg"
                        disabled={isSubmitting}
                        style={{ minWidth: '200px' }}
                      >
                        {isSubmitting ? 'Enviando...' : t('contactForm.send_button')}
                      </Button>
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default PantallasTactiles;
